import { useCallback, useMemo, useState } from 'react'

import { SelectFilter } from 'components/Filters/SelectFilter'
import { UrlSearchParam } from 'consts/url'
import { useUrlSearchParams } from 'hooks/useUrlSearchParams'
import { useChannelsQuery } from 'modules/api/channels/queries'
import { Region, RegionStore } from 'modules/region/region'
import { Channel } from 'types'
import { deriveLoadingStateFromQueryStatus } from 'utils/deriveLoadingStateFromQueryStatus'
import { humanize } from 'utils/format'

const INDIA_PREFIX = 'IN-'
const withIndiaPrefix = (value: string) => `${INDIA_PREFIX}${value}`

export const useChannelsFilter = () => {
  const { urlSearchParams, setUrlSearchParams } = useUrlSearchParams({
    replace: true,
  })
  const [value, setValue] = useState(() => {
    if (!urlSearchParams.channel?.[0]) {
      return Channel.Dante
    }

    if (urlSearchParams.region?.[0] === Region.IN) {
      return withIndiaPrefix(urlSearchParams.channel[0])
    }

    return urlSearchParams.channel[0]
  })

  const { data: channels = [], status: channelsStatus } = useChannelsQuery()

  const channelOptions = useMemo(
    () =>
      channels
        .map(channelValue => [
          {
            key: channelValue,
            value: channelValue,
            label: humanize(channelValue),
          },
          {
            key: withIndiaPrefix(channelValue),
            value: withIndiaPrefix(channelValue),
            label: `${humanize(channelValue)} India`,
          },
        ])
        .flat(),
    [channels],
  )

  const handleChange = useCallback(
    (newValue: string | null) => {
      if (!newValue) {
        return
      }

      const newChannel = newValue.replace(INDIA_PREFIX, '')
      setValue(newValue)

      if (newValue.includes(INDIA_PREFIX)) {
        setUrlSearchParams({
          [UrlSearchParam.Channel]: newChannel,
          [UrlSearchParam.Region]: Region.IN,
        })
        RegionStore.setRegion(Region.IN)
      } else {
        setUrlSearchParams({
          [UrlSearchParam.Channel]: newChannel,
          [UrlSearchParam.Region]: Region.EU,
        })
        RegionStore.setRegion(Region.EU)
      }
    },
    [setUrlSearchParams],
  )

  return {
    Filter: () => (
      <SelectFilter
        value={value}
        label="Channels"
        options={channelOptions}
        loadingState={deriveLoadingStateFromQueryStatus(channelsStatus)}
        onChange={handleChange}
      />
    ),

    isPending: channelsStatus === 'pending',
  }
}
